import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './OzelFiyatlandirma.css';

const parseMoney = (value) => {
  const text = String(value || '').trim();
  const isUsd = text.includes('$') || text.toUpperCase().includes('USD');
  const isFree = /ucretsiz|ücretsiz/i.test(text);
  if (isFree) return { amount: 0, currency: 'FREE' };

  const numeric = text.replace(/[^\d.,-]/g, '');
  let normalized = numeric;
  const hasDot = normalized.includes('.');
  const hasComma = normalized.includes(',');

  if (hasDot && hasComma) {
    const lastDot = normalized.lastIndexOf('.');
    const lastComma = normalized.lastIndexOf(',');
    normalized = lastComma > lastDot
      ? normalized.replace(/\./g, '').replace(',', '.')
      : normalized.replace(/,/g, '');
  } else if (hasComma) {
    normalized = normalized.replace(',', '.');
  } else if (hasDot) {
    const parts = normalized.split('.');
    if (parts.length > 1 && parts[parts.length - 1].length === 3) {
      normalized = normalized.replace(/\./g, '');
    }
  }

  const amount = Number(normalized);
  return { amount: Number.isFinite(amount) ? amount : 0, currency: isUsd ? 'USD' : 'TRY' };
};

const formatCurrency = (amount, currency) => new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: currency === 'USD' ? 'USD' : 'TRY',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
}).format(amount || 0);

const addMoney = (base, value, multiplier = 1) => {
  const parsed = parseMoney(value);
  if (parsed.currency === 'USD') return { ...base, USD: base.USD + (parsed.amount * multiplier) };
  if (parsed.currency === 'TRY') return { ...base, TRY: base.TRY + (parsed.amount * multiplier) };
  return base;
};

const moneyText = (totals) => {
  const parts = [];
  if (totals.TRY > 0) parts.push(formatCurrency(totals.TRY, 'TRY'));
  if (totals.USD > 0) parts.push(formatCurrency(totals.USD, 'USD'));
  return parts.length ? parts.join(' + ') : formatCurrency(0, 'TRY');
};

const findOtherItem = (items, keyword) => items.find((item) => item.label?.toLowerCase().includes(keyword));
const isServerSetupItem = (label) => (
  label === 'ISP Radius Sunucularına Kurulum' || label === 'Kendi Sunucularınıza Kurulum'
);

const calcTieredTotals = (count, tiers) => {
  let remaining = Number(count || 0);
  if (!remaining || !Array.isArray(tiers) || tiers.length === 0) {
    return { totals: { TRY: 0, USD: 0 }, breakdown: [] };
  }

  const sorted = [...tiers].sort((a, b) => Number(a.min || 0) - Number(b.min || 0));
  let totals = { TRY: 0, USD: 0 };
  const breakdown = [];
  let prevEnd = null;

  for (let i = 0; i < sorted.length; i += 1) {
    const tier = sorted[i];
    if (remaining <= 0) break;
    const min = Number(tier.min || 0);
    const max = tier.max === null || tier.max === '' || tier.max === undefined ? Infinity : Number(tier.max);
    let start = min;

    if (prevEnd === null && start <= 0) {
      start = 1;
    } else if (prevEnd !== null) {
      start = Math.max(start, prevEnd + 1);
    }

    const tierCapacity = max === Infinity ? remaining : Math.max(0, max - start + 1);
    if (tierCapacity <= 0) continue;
    const qty = Math.min(remaining, tierCapacity);
    totals = addMoney(totals, tier.price_per_subscriber, qty);
    breakdown.push({
      tierIndex: i,
      rangeLabel: max === Infinity ? `${start}+` : `${start}-${max}`,
      qty,
      unitPrice: tier.price_per_subscriber
    });
    remaining -= qty;
    prevEnd = max === Infinity ? prevEnd : max;
  }

  return { totals, breakdown };
};

const OzelFiyatlandirma = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCount, setActiveCount] = useState('');
  const [passiveCount, setPassiveCount] = useState('');
  const [frozenCount, setFrozenCount] = useState('');
  const [cancelledCount, setCancelledCount] = useState('');
  const [selectedSetupKeys, setSelectedSetupKeys] = useState([]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch('/special-pricing.json');
        if (!response.ok) throw new Error('JSON dosyasi okunamadi');
        setConfig((await response.json()) || {});
      } catch (fetchError) {
        setError('Fiyatlandirma verisi yuklenemedi.');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const setupItems = config?.setup_items || [];
  const monthlyFixedItems = config?.monthly_fixed_items || [];
  const otherItems = config?.other_items || [];
  const tiers = config?.subscriber_tiers || [];

  const activeTiered = useMemo(() => calcTieredTotals(Number(activeCount || 0), tiers), [activeCount, tiers]);

  const fixedMonthlyTotals = useMemo(
    () => monthlyFixedItems.reduce((acc, item) => addMoney(acc, item.amount), { TRY: 0, USD: 0 }),
    [monthlyFixedItems]
  );

  const passiveItem = findOtherItem(otherItems, 'pasif');
  const frozenItem = findOtherItem(otherItems, 'dondur');
  const cancelledItem = findOtherItem(otherItems, 'iptal');

  const activeTotals = activeTiered.totals;
  const passiveTotals = useMemo(
    () => addMoney({ TRY: 0, USD: 0 }, passiveItem?.amount, Number(passiveCount || 0)),
    [passiveItem, passiveCount]
  );
  const frozenTotals = useMemo(
    () => addMoney({ TRY: 0, USD: 0 }, frozenItem?.amount, Number(frozenCount || 0)),
    [frozenItem, frozenCount]
  );
  const cancelledTotals = useMemo(
    () => addMoney({ TRY: 0, USD: 0 }, cancelledItem?.amount, Number(cancelledCount || 0)),
    [cancelledItem, cancelledCount]
  );

  const selectedSetupTotals = useMemo(
    () => setupItems.reduce((acc, item) => {
      const key = `${item.label}-${item.amount}`;
      return selectedSetupKeys.includes(key) ? addMoney(acc, item.amount) : acc;
    }, { TRY: 0, USD: 0 }),
    [setupItems, selectedSetupKeys]
  );

  const monthlySubtotal = {
    TRY: fixedMonthlyTotals.TRY + activeTotals.TRY + passiveTotals.TRY + frozenTotals.TRY + cancelledTotals.TRY,
    USD: fixedMonthlyTotals.USD + activeTotals.USD + passiveTotals.USD + frozenTotals.USD + cancelledTotals.USD
  };

  const firstPaymentSubtotal = selectedSetupTotals;

  const toggleSetupSelection = (item) => {
    const key = `${item.label}-${item.amount}`;
    setSelectedSetupKeys((prev) => {
      const alreadySelected = prev.includes(key);
      if (alreadySelected) {
        return prev.filter((entry) => entry !== key);
      }

      if (isServerSetupItem(item.label)) {
        const filtered = prev.filter((entry) => {
          const matchedItem = setupItems.find((setup) => `${setup.label}-${setup.amount}` === entry);
          const selectedLabel = matchedItem?.label || '';
          return !isServerSetupItem(selectedLabel);
        });
        return [...filtered, key];
      }

      return [...prev, key];
    });
  };

  const resetSetupSelections = () => setSelectedSetupKeys([]);
  const resetSubscriberCounts = () => {
    setActiveCount('');
    setPassiveCount('');
    setFrozenCount('');
    setCancelledCount('');
  };

  if (loading) return <div className="pricing-page-state">Yukleniyor...</div>;
  if (error) return <div className="pricing-page-state pricing-page-error">{error}</div>;
  if (!config?.is_active) return <div className="pricing-page-state">Bu sayfa aktif degil.</div>;

  return (
    <div className="pricing-page">
      <Helmet>
        <title>Fiyatlandirma</title>
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
      </Helmet>

      <div className="pricing-page-header">
        <div className="pricing-header-title">
          <img src="/web-theme/images/logo-siyah.png" alt="ISP Radius" className="pricing-header-logo" />
          <div className="pricing-header-text">
            <h1>{config.title || 'FİYATLANDIRMA'}</h1>
            <p>Önce fiyatlandırma kalemlerini inceleyin, ardından abone adetlerini girerek hesaplama alın.</p>
          </div>
        </div>
      </div>

      <section className="pricing-card summary-card">
        <div>
          <p>Ilk Odeme</p>
          <h3>{moneyText(firstPaymentSubtotal)}</h3>
          <small>Kurulum secimlerinden olusur</small>
        </div>
        <div>
          <p>Aylik Fatura</p>
          <h3>{moneyText(monthlySubtotal)}</h3>
          <small>Aylik sabit + abone bazli toplam</small>
        </div>
      </section>

      <div className="top-grid">
        <section className="pricing-card">
          <h2>Kurulum Ucretleri</h2>
          <p className="tiered-note">
            Sunucu Kurulumu seceneginde sadece bir tanesi secilebilir.
          </p>
          <div className="section-actions">
            <button type="button" onClick={resetSetupSelections}>Kurulum secimlerini sifirla</button>
          </div>
          <table className="pricing-table">
            <thead><tr><th>Kalem</th><th>Tutar</th><th>Periyot</th><th>Durum</th></tr></thead>
            <tbody>
              {setupItems.map((item, index) => {
                const key = `${item.label}-${item.amount}`;
                return (
                  <tr key={`setup-${index}`}>
                    <td>
                      <label className="setup-select">
                        <input type="checkbox" checked={selectedSetupKeys.includes(key)} onChange={() => toggleSetupSelection(item)} />
                        {item.label}
                      </label>
                    </td>
                    <td>{item.amount}</td>
                    <td>{item.period || '-'}</td>
                    <td>{item.optional ? 'Opsiyonel' : 'Zorunlu'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        <section className="pricing-card">
          <h2>Hesaplama Araci</h2>
          <p className="tiered-note">
            Aktif abone maliyeti kademeli (tiered) fiyatlandirma modeli ile hesaplanir.
          </p>
          <div className="section-actions">
            <button type="button" onClick={resetSubscriberCounts}>Abone adetlerini sifirla</button>
          </div>

          <div className="calc-split">
            <div className="calc-pane">
              <h3>Abone Adetleri</h3>
              <div className="calc-grid">
                <label>Aktif Abone<input type="number" min="0" value={activeCount} onChange={(e) => setActiveCount(e.target.value)} /></label>
                <label>Pasif Abone<input type="number" min="0" value={passiveCount} onChange={(e) => setPassiveCount(e.target.value)} /></label>
                <label>Dondurulan Abone<input type="number" min="0" value={frozenCount} onChange={(e) => setFrozenCount(e.target.value)} /></label>
                <label>Iptal Abone<input type="number" min="0" value={cancelledCount} onChange={(e) => setCancelledCount(e.target.value)} /></label>
              </div>
            </div>
            <div className="calc-pane">
              <h3>Hesaplama Detayi</h3>
              <div className="calc-lines">
                <div><span>Aylik sabit kalemler</span><strong>{moneyText(fixedMonthlyTotals)}</strong></div>
                <div><span>Aktif aboneler (kademeli)</span><strong>{moneyText(activeTotals)}</strong></div>
                <div><span>Pasif aboneler</span><strong>{moneyText(passiveTotals)}</strong></div>
                <div><span>Dondurulan aboneler</span><strong>{moneyText(frozenTotals)}</strong></div>
                <div><span>Iptal aboneler</span><strong>{moneyText(cancelledTotals)}</strong></div>
                <div><span>Secilen kurulum kalemleri (ilk odeme)</span><strong>{moneyText(selectedSetupTotals)}</strong></div>
              </div>
            </div>
          </div>

          <div className="calc-total">
            <div>
              <p>Ilk Odeme (Sadece Kurulum Secimleri)</p>
              <h3>{moneyText(firstPaymentSubtotal)}</h3>
              <small>Net tutar (KDV haric)</small>
            </div>
            <div>
              <p>Diger Aylar Aylik Fatura</p>
              <h3>{moneyText(monthlySubtotal)}</h3>
              <small>Net tutar (KDV haric)</small>
            </div>
          </div>
        </section>
      </div>

      <section className="pricing-card">
        <h2>Aylik Sabit Faturalandirma</h2>
        <table className="pricing-table">
          <thead><tr><th>Kalem</th><th>Tutar</th><th>Periyot</th></tr></thead>
          <tbody>
            {monthlyFixedItems.map((item, index) => (
              <tr key={`fixed-${index}`}>
                <td>{item.label}</td>
                <td>{item.amount}</td>
                <td>{item.period || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="pricing-card">
        <h2>Aktif Abone Paketleri (Birim Fiyatlar)</h2>
        <table className="pricing-table">
          <thead><tr><th>Abone Araligi</th><th>Fiyat / Abone</th><th>Periyot</th><th>Hesapta Kullanilan</th></tr></thead>
          <tbody>
            {tiers.map((tier, index) => (
              <tr key={`tier-${index}`} className={activeTiered.breakdown.some((b) => b.tierIndex === index) ? 'active-tier' : ''}>
                <td>{tier.max === null ? `${tier.min}+` : `${tier.min} - ${tier.max}`}</td>
                <td>{tier.price_per_subscriber}</td>
                <td>{tier.period || '-'}</td>
                <td>
                  {(() => {
                    const used = activeTiered.breakdown.find((b) => b.tierIndex === index);
                    return used ? `${used.qty} abone` : '-';
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="pricing-card">
        <h2>Diger Durumlar</h2>
        <table className="pricing-table">
          <thead><tr><th>Kalem</th><th>Tutar</th><th>Periyot</th><th>Hesapta Kullanilan</th></tr></thead>
          <tbody>
            {otherItems.map((item, index) => (
              <tr key={`other-${index}`}>
                <td>{item.label}</td>
                <td>{item.amount}</td>
                <td>{item.period || '-'}</td>
                <td>
                  {item.label?.toLowerCase().includes('pasif')
                    ? (Number(passiveCount || 0) > 0 ? `${Number(passiveCount || 0)} abone` : '-')
                    : item.label?.toLowerCase().includes('dondur')
                      ? (Number(frozenCount || 0) > 0 ? `${Number(frozenCount || 0)} abone` : '-')
                      : item.label?.toLowerCase().includes('iptal')
                        ? (Number(cancelledCount || 0) > 0 ? `${Number(cancelledCount || 0)} abone` : '-')
                        : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {(config.notes || []).length > 0 && (
        <section className="pricing-card">
          <h2>Notlar</h2>
          <ul className="pricing-notes">
            {config.notes.map((note, index) => <li key={`note-${index}`}>{note.text}</li>)}
          </ul>
        </section>
      )}
    </div>
  );
};

export default OzelFiyatlandirma;
